import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit
} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import {merge} from 'rxjs';
import {takeUntil, tap} from 'rxjs/operators';

import * as moment from 'moment';

import {
  DEFAULT_WEEK_START,
  HrCalendarDay,
  HrCalendarMonth,
  HrCalendarTag,
  HrCalendarTagsByDate,
  HrCalendarWeek,
  HrCalendarWorkSchedule,
  HrCalendarWorkScheduleByDate,
  HrCalendarWorkScheduleDay,
  HR_CALENDAR_MAX_TAGS,
  WeekStartDay
} from './hr-calendar.models';

import {HrCalendarGridUtil} from '@gp/shared/hr-calendar/hr-calendar-utils/hr-calendar-grid.util';
import {HrCalendarWorkScheduleUtil} from '@gp/shared/hr-calendar/hr-calendar-utils/hr-calendar-work-schedule.util';
import {Destroyable} from '../../destroyable.component';
import {LocalizationService} from '../localization/localization.service';

const WEEKDAY_TRANSLATION_KEYS: Record<number, string> = {
  0: 'PAYROLL_PREPARE.Sun',
  1: 'PAYROLL_PREPARE.Mon',
  2: 'PAYROLL_PREPARE.Tue',
  3: 'PAYROLL_PREPARE.Wed',
  4: 'PAYROLL_PREPARE.Thu',
  5: 'PAYROLL_PREPARE.Fri',
  6: 'PAYROLL_PREPARE.Sat'
};

const NO_TAGS: HrCalendarTag[] = [];

@Component({
  selector: 'gp-hr-calendar',
  templateUrl: './hr-calendar.component.html',
  styleUrls: ['./hr-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HrCalendarComponent extends Destroyable implements OnInit, OnChanges {

  @Input()
  public tags: HrCalendarTagsByDate = {};

  @Input()
  public weekStart: WeekStartDay = DEFAULT_WEEK_START;

  @Input()
  public workSchedules: HrCalendarWorkSchedule[] = [];

  public viewDate: Date = HrCalendarGridUtil.firstOfMonth(new Date());
  public month: HrCalendarMonth;
  public monthLabel = '';

  private visibleTagsByDate: HrCalendarTagsByDate = {};
  private workScheduleByDate: HrCalendarWorkScheduleByDate = {};

  constructor(private translate: TranslateService,
              private localization: LocalizationService,
              private cd: ChangeDetectorRef) {
    super();
  }

  public ngOnInit(): void {
    this.rebuild();
    merge(
      this.translate.onLangChange,
      this.localization.changeNumberFormatEvent
    ).pipe(
      tap(() => {
        this.rebuild();
        this.cd.markForCheck();
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public ngOnChanges(): void {
    this.rebuild();
  }

  public previousMonth(): void {
    this.showMonth(HrCalendarGridUtil.addMonths(this.viewDate, -1));
  }

  public nextMonth(): void {
    this.showMonth(HrCalendarGridUtil.addMonths(this.viewDate, 1));
  }

  public selectMonth(date: Date): void {
    this.showMonth(HrCalendarGridUtil.firstOfMonth(date));
  }

  public visibleTags(day: HrCalendarDay): HrCalendarTag[] {
    return this.visibleTagsByDate[day.isoDate] ?? NO_TAGS;
  }

  public workScheduleFor(day: HrCalendarDay): HrCalendarWorkScheduleDay | null {
    return this.workScheduleByDate[day.isoDate] ?? null;
  }

  public weekdayKey(dayOfWeek: number): string {
    return WEEKDAY_TRANSLATION_KEYS[dayOfWeek];
  }

  public isWeekendDay(dayOfWeek: number): boolean {
    return dayOfWeek === 0 || dayOfWeek === 6;
  }

  public trackWeek(_: number, week: HrCalendarWeek): string {
    return week.days[0].isoDate;
  }

  public trackDay(_: number, day: HrCalendarDay): string {
    return day.isoDate;
  }

  private showMonth(viewDate: Date): void {
    this.viewDate = viewDate;
    this.rebuild();
    this.cd.markForCheck();
  }

  private rebuild(): void {
    this.month = HrCalendarGridUtil.buildMonth(this.viewDate, this.weekStart ?? DEFAULT_WEEK_START);
    this.monthLabel = moment(this.viewDate).format('MMMM YYYY');
    this.visibleTagsByDate = Object.entries(this.tags ?? {}).reduce((acc, [isoDate, dateTags]) => {
      acc[isoDate] = (dateTags ?? []).slice(0, HR_CALENDAR_MAX_TAGS);
      return acc;
    }, {} as HrCalendarTagsByDate);
    this.workScheduleByDate = HrCalendarWorkScheduleUtil.resolveDays(this.month.weeks, this.workSchedules);
  }
}
